import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  AlertCircle,
  Lock,
  Wallet,
  FileText,
  Shield,
  CheckCircle2,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 'amount', label: 'Monto', icon: Wallet },
  { id: 'summary', label: 'Resumen', icon: FileText },
  { id: 'risks', label: 'Riesgos', icon: Shield },
  { id: 'confirm', label: 'Confirmar', icon: CheckCircle2 },
];

const CommitmentPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { formatCurrency } = useI18n();
  const navigate = useNavigate();
  
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [commitment, setCommitment] = useState(null);
  
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'MXN',
    investment_source: '',
    notes: '',
    acknowledged_risks: false,
    acknowledged_terms: false,
    acknowledged_no_guarantee: false,
    acknowledged_illiquidity: false,
  });

  const isVerified = user?.verification_status === 'approved';

  useEffect(() => {
    fetchOpportunity();
  }, [slug]);

  const fetchOpportunity = async () => {
    try {
      const response = await api.get(`/opportunities/${slug}`);
      setOpportunity(response.data);
      
      // Check if user already has a commitment
      if (response.data.user_commitment) {
        setCommitment(response.data.user_commitment);
      }
    } catch (error) {
      console.error('Error fetching opportunity:', error);
      toast.error('Error al cargar la oportunidad');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Amount
        const amount = parseFloat(formData.amount);
        if (!amount || isNaN(amount)) {
          toast.error('Ingrese un monto válido');
          return false;
        }
        if (amount < opportunity.minimum_investment) {
          toast.error(`El monto mínimo es ${formatCurrency(opportunity.minimum_investment)}`);
          return false;
        }
        if (opportunity.maximum_investment && amount > opportunity.maximum_investment) {
          toast.error(`El monto máximo es ${formatCurrency(opportunity.maximum_investment)}`);
          return false;
        }
        if (!formData.investment_source) {
          toast.error('Seleccione la fuente de fondos');
          return false;
        }
        return true;
      case 1: // Summary - just review, no validation needed
        return true;
      case 2: // Risks
        if (!formData.acknowledged_risks || !formData.acknowledged_no_guarantee || !formData.acknowledged_illiquidity) {
          toast.error('Debe reconocer todos los riesgos para continuar');
          return false;
        }
        return true;
      case 3: // Confirm
        if (!formData.acknowledged_terms) {
          toast.error('Debe aceptar los términos y condiciones');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep === 3) {
        submitCommitment();
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const submitCommitment = async () => {
    setSubmitting(true);
    try {
      const response = await api.post('/commitments', {
        opportunity_id: opportunity.opportunity_id,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        investment_source: formData.investment_source,
        notes: formData.notes,
        acknowledged_risks: formData.acknowledged_risks,
        acknowledged_terms: formData.acknowledged_terms,
        acknowledged_no_guarantee: formData.acknowledged_no_guarantee,
        acknowledged_illiquidity: formData.acknowledged_illiquidity,
      });
      
      toast.success('Compromiso de inversión creado exitosamente');
      setCommitment(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el compromiso');
    } finally {
      setSubmitting(false);
    }
  };

  const stepProgress = ((currentStep + 1) / STEPS.length) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <Lock className="h-16 w-16 mx-auto mb-6 text-amber-500" strokeWidth={1} />
        <h1 className="heading-3 mb-4">Verificación Requerida</h1>
        <p className="body-text mb-8">
          Para invertir en este proyecto, primero debe completar su proceso de verificación KYC.
        </p>
        <div className="flex gap-4 justify-center">
          <Link to={`/app/opportunities/${slug}`}>
            <Button variant="outline">Volver al Proyecto</Button>
          </Link>
          <Link to="/app/profile">
            <Button className="btn-primary">Completar Verificación</Button>
          </Link>
        </div>
      </div>
    );
  }

  // If user already has a commitment, show status
  if (commitment) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="commitment-status-page">
        <Link 
          to={`/app/opportunities/${slug}`} 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          Volver al proyecto
        </Link>

        <Card className="card-dashboard">
          <CardHeader className="text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-emerald-500" strokeWidth={1} />
            <CardTitle className="heading-3">Compromiso Registrado</CardTitle>
            <CardDescription>
              Su compromiso de inversión ha sido registrado exitosamente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-6 bg-muted/30 rounded-sm space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Proyecto</span>
                <span className="font-medium">{opportunity?.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monto</span>
                <span className="font-medium text-primary">{formatCurrency(commitment.amount, commitment.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estado</span>
                <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                  {commitment.status === 'pending' ? 'Pendiente de Revisión' : 
                   commitment.status === 'approved' ? 'Aprobado' :
                   commitment.status === 'funds_pending' ? 'Esperando Fondos' :
                   commitment.status === 'confirmed' ? 'Confirmado' : commitment.status}
                </Badge>
              </div>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Siguiente paso:</strong> Nuestro equipo revisará su compromiso. Una vez aprobado, recibirá instrucciones para transferir los fondos.
              </AlertDescription>
            </Alert>

            <div className="flex gap-4">
              <Link to="/app/investments" className="flex-1">
                <Button className="w-full btn-outline">Ver Mis Inversiones</Button>
              </Link>
              <Link to="/app/opportunities" className="flex-1">
                <Button className="w-full btn-primary">Explorar Más Proyectos</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          
          return (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div className={`
                h-10 w-10 rounded-full flex items-center justify-center border-2 transition-colors
                ${isCompleted ? 'bg-primary border-primary text-primary-foreground' : ''}
                ${isCurrent ? 'border-primary text-primary' : ''}
                ${!isCompleted && !isCurrent ? 'border-muted text-muted-foreground' : ''}
              `}>
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
                ) : (
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                )}
              </div>
              <span className={`text-xs mt-2 text-center hidden md:block ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      <Progress value={stepProgress} className="h-1" />
    </div>
  );

  const renderAmountStep = () => (
    <div className="space-y-6">
      <Alert>
        <Wallet className="h-4 w-4" />
        <AlertDescription>
          Está invirtiendo en: <strong>{opportunity?.title}</strong>
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="amount">Monto de Inversión *</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input
              id="amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              placeholder="0.00"
              className="pl-8"
              min={opportunity?.minimum_investment}
              max={opportunity?.maximum_investment}
              data-testid="commitment-amount"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Mínimo: {formatCurrency(opportunity?.minimum_investment)}
            {opportunity?.maximum_investment && ` • Máximo: ${formatCurrency(opportunity?.maximum_investment)}`}
          </p>
        </div>

        <div className="space-y-2">
          <Label>Moneda</Label>
          <Select
            value={formData.currency}
            onValueChange={(v) => handleInputChange('currency', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
              <SelectItem value="USD">USD - Dólar Americano</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="source">Fuente de Fondos *</Label>
        <Select
          value={formData.investment_source}
          onValueChange={(v) => handleInputChange('investment_source', v)}
        >
          <SelectTrigger data-testid="investment-source">
            <SelectValue placeholder="Seleccione la fuente de fondos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="savings">Ahorros personales</SelectItem>
            <SelectItem value="business_income">Ingresos de negocio</SelectItem>
            <SelectItem value="investment_returns">Rendimientos de inversiones</SelectItem>
            <SelectItem value="inheritance">Herencia</SelectItem>
            <SelectItem value="sale_of_assets">Venta de activos</SelectItem>
            <SelectItem value="other">Otro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notas adicionales (opcional)</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder="Cualquier información adicional que desee compartir..."
          rows={3}
        />
      </div>
    </div>
  );

  const renderSummaryStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Resumen del Compromiso</h3>
      
      <Card className="card-dashboard">
        <CardContent className="p-6 space-y-4">
          <div className="flex justify-between py-3 border-b border-border">
            <span className="text-muted-foreground">Proyecto</span>
            <span className="font-medium">{opportunity?.title}</span>
          </div>
          <div className="flex justify-between py-3 border-b border-border">
            <span className="text-muted-foreground">Ubicación</span>
            <span>{opportunity?.location_city}, {opportunity?.location_state}</span>
          </div>
          <div className="flex justify-between py-3 border-b border-border">
            <span className="text-muted-foreground">Monto de Inversión</span>
            <span className="font-medium text-primary text-xl">
              {formatCurrency(parseFloat(formData.amount) || 0, formData.currency)}
            </span>
          </div>
          <div className="flex justify-between py-3 border-b border-border">
            <span className="text-muted-foreground">TIR Objetivo</span>
            <span>{opportunity?.projected_irr_base}-{opportunity?.projected_irr_upside}%</span>
          </div>
          <div className="flex justify-between py-3 border-b border-border">
            <span className="text-muted-foreground">Plazo Estimado</span>
            <span>{opportunity?.hold_period_months} meses</span>
          </div>
          <div className="flex justify-between py-3">
            <span className="text-muted-foreground">Fuente de Fondos</span>
            <span className="capitalize">{formData.investment_source?.replace(/_/g, ' ')}</span>
          </div>
        </CardContent>
      </Card>

      <Alert className="border-muted">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Revise cuidadosamente los detalles antes de continuar. En el siguiente paso deberá reconocer los riesgos asociados a esta inversión.
        </AlertDescription>
      </Alert>
    </div>
  );

  const renderRisksStep = () => (
    <div className="space-y-6">
      <Alert className="border-red-500/20 bg-red-500/5">
        <AlertCircle className="h-4 w-4 text-red-500" />
        <AlertDescription className="text-red-400">
          <strong>ADVERTENCIA DE RIESGO IMPORTANTE</strong>
          <br />
          Lea cuidadosamente cada punto antes de continuar.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div className="p-4 border border-border rounded-sm space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="risk1"
              checked={formData.acknowledged_risks}
              onCheckedChange={(checked) => handleInputChange('acknowledged_risks', checked)}
              data-testid="ack-risks"
            />
            <Label htmlFor="risk1" className="text-sm cursor-pointer leading-relaxed">
              <strong>Reconozco que la inversión en bienes raíces conlleva riesgos significativos</strong>, incluyendo pero no limitado a: riesgo de mercado, riesgo de liquidez, riesgo de construcción, riesgo regulatorio y riesgo de pérdida total del capital invertido.
            </Label>
          </div>
        </div>

        <div className="p-4 border border-border rounded-sm space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="risk2"
              checked={formData.acknowledged_no_guarantee}
              onCheckedChange={(checked) => handleInputChange('acknowledged_no_guarantee', checked)}
              data-testid="ack-no-guarantee"
            />
            <Label htmlFor="risk2" className="text-sm cursor-pointer leading-relaxed">
              <strong>Entiendo que los rendimientos proyectados NO están garantizados</strong>. Los rendimientos pasados no garantizan resultados futuros. Las proyecciones son estimaciones basadas en supuestos que pueden no cumplirse.
            </Label>
          </div>
        </div>

        <div className="p-4 border border-border rounded-sm space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="risk3"
              checked={formData.acknowledged_illiquidity}
              onCheckedChange={(checked) => handleInputChange('acknowledged_illiquidity', checked)}
              data-testid="ack-illiquidity"
            />
            <Label htmlFor="risk3" className="text-sm cursor-pointer leading-relaxed">
              <strong>Entiendo que esta inversión es ILÍQUIDA</strong>. No podré retirar mis fondos hasta el término del plazo del proyecto. No existe un mercado secundario garantizado para vender mi participación.
            </Label>
          </div>
        </div>
      </div>

      <div className="p-4 bg-muted/30 rounded-sm">
        <p className="text-sm text-muted-foreground">
          Al continuar, confirmo que he leído y entiendo completamente los riesgos asociados con esta inversión y que estoy invirtiendo fondos que puedo permitirme perder en su totalidad.
        </p>
      </div>
    </div>
  );

  const renderConfirmStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <Wallet className="h-16 w-16 mx-auto mb-4 text-primary" strokeWidth={1} />
        <h3 className="text-lg font-medium mb-2">Confirmar Compromiso de Inversión</h3>
        <p className="text-muted-foreground">
          Está a punto de comprometer <strong className="text-primary">{formatCurrency(parseFloat(formData.amount), formData.currency)}</strong> en {opportunity?.title}
        </p>
      </div>

      <Card className="card-dashboard">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Checkbox
              id="terms"
              checked={formData.acknowledged_terms}
              onCheckedChange={(checked) => handleInputChange('acknowledged_terms', checked)}
              data-testid="ack-terms"
            />
            <Label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed">
              He leído y acepto los <Link to="/legal/terms" className="text-primary underline" target="_blank">Términos y Condiciones</Link>, la{' '}
              <Link to="/legal/privacy" className="text-primary underline" target="_blank">Política de Privacidad</Link> y las{' '}
              <Link to="/legal/risk-disclosures" className="text-primary underline" target="_blank">Divulgaciones de Riesgo</Link>. Confirmo que toda la información proporcionada es verdadera y completa.
            </Label>
          </div>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Proceso posterior:</strong> Una vez confirmado su compromiso, nuestro equipo lo revisará y se comunicará con usted con las instrucciones para la transferencia de fondos.
        </AlertDescription>
      </Alert>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderAmountStep();
      case 1: return renderSummaryStep();
      case 2: return renderRisksStep();
      case 3: return renderConfirmStep();
      default: return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="commitment-page">
      <Link 
        to={`/app/opportunities/${slug}`} 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        Volver al proyecto
      </Link>

      <div className="mb-8">
        <h1 className="heading-2 mb-2">Compromiso de Inversión</h1>
        <p className="text-muted-foreground">Complete el proceso para registrar su compromiso.</p>
      </div>

      {renderStepIndicator()}

      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle>{STEPS[currentStep].label}</CardTitle>
        </CardHeader>
        <CardContent>
          {renderCurrentStep()}

          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              Anterior
            </Button>
            
            <Button
              onClick={handleNext}
              disabled={submitting}
              className="btn-primary gap-2"
              data-testid="commitment-next"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : currentStep === 3 ? (
                <>
                  Confirmar Compromiso
                  <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
                </>
              ) : (
                <>
                  Siguiente
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommitmentPage;
